import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { CalendarDays, MapPin, Clock, Sparkles, CalendarPlus, CheckCircle2 } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/b659007a-b26e-4c48-80c7-ae6e37053ed8/images/77129cbb523c421d44bc7439a9f1643b76f56a8817039031927f3736d4f01e55.png";

export default function Info() {
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const token = localStorage.getItem("ebel_token");

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data));
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!info) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-[#4A4F46] font-serif text-2xl">Einen Moment …</div>;
  }

  return (
    <div data-testid="info-page">
      {/* Hero */}
      <section
        className="relative px-6 lg:px-10 pt-20 pb-28"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(253,251,247,0.5), rgba(253,251,247,1)), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-4xl mx-auto text-center fade-in">
          <p className="label-eyebrow mb-5">Herzlich Willkommen</p>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-[1.02] text-[#1A1C19]">
            {info.title.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="italic text-[#48634A]">{info.title.split(" ").slice(-1)}</span>
          </h1>
          <p className="font-serif text-2xl lg:text-3xl italic text-[#4A4F46] mt-4">{info.subtitle}</p>
          <div className="divider-ornament max-w-sm mx-auto mt-10">
            <Sparkles className="w-4 h-4" />
          </div>

          {/* Call to action: kalender + zusagen-counter */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`${API}/calendar.ics?auth=${token}`}
              className="btn-primary rounded-full px-6 py-3 inline-flex items-center gap-2 text-sm tracking-wide"
              data-testid="add-to-calendar-btn"
            >
              <CalendarPlus className="w-4 h-4" />
              Zum Kalender hinzufügen
            </a>
            {stats && stats.total > 0 && (
              <div
                className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-[#E5DFD3] rounded-full px-5 py-2.5 text-sm text-[#1A1C19]"
                data-testid="rsvp-counter"
              >
                <CheckCircle2 className="w-4 h-4 text-[#48634A]" />
                <span>
                  <span className="font-serif text-lg text-[#48634A] mr-1">{stats.ja}</span>
                  von {stats.total} {stats.total === 1 ? "Familie hat" : "Familien haben"} bereits zugesagt
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Key facts */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 -mt-8 mb-20 relative z-10">
        <div className="grid md:grid-cols-3 gap-6">
          <Fact icon={<CalendarDays className="w-5 h-5" />} eyebrow="Termin" value={info.event_date} accent="#C97B5D" tint="#FDE8DC" />
          <Fact icon={<Clock className="w-5 h-5" />} eyebrow="An und Abreise" value={info.event_time} accent="#3F6E96" tint="#E0ECF6" />
          <Fact icon={<MapPin className="w-5 h-5" />} eyebrow="Ort" value={info.location} sub={info.address} accent="#3F7A42" tint="#DCEADD" />
        </div>
      </section>

      {/* Description */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-12 text-center">
        <p className="label-eyebrow mb-4">Liebe Familie</p>
        <p className="font-serif text-2xl lg:text-3xl leading-relaxed text-[#1A1C19]">
          {info.description}
        </p>
      </section>

      {/* Program */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-center mb-10">
          <p className="label-eyebrow mb-3">Programm</p>
          <h2 className="font-serif text-4xl tracking-tight">Unser Wochenende</h2>
        </div>
        <ol className="space-y-5">
          {(info.program || []).map((p, i) => (
            <li key={`${i}-${p}`} className="flex gap-5 items-start pb-5 border-b border-[#E5DFD3] last:border-b-0" data-testid={`program-item-${i}`}>
              <span className="font-serif text-3xl text-[#48634A] leading-none w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-lg text-[#1A1C19] leading-relaxed">{p}</span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function Fact({ icon, eyebrow, value, sub, accent = "#48634A", tint = "#FFFFFF" }) {
  return (
    <div
      className="rounded-2xl overflow-hidden shadow-[0_12px_40px_-20px_rgba(72,99,74,0.35)] transition-transform hover:-translate-y-1 bg-white border"
      style={{ borderColor: `${accent}55` }}
    >
      {/* Kräftiger Farbkopf mit Label */}
      <div
        className="px-6 py-3 flex items-center gap-2 text-white"
        style={{ backgroundColor: accent }}
      >
        {icon}
        <span
          className="font-semibold uppercase tracking-[0.18em]"
          style={{ fontSize: "0.85rem" }}
        >
          {eyebrow}
        </span>
      </div>
      {/* Wert */}
      <div className="p-6 text-left" style={{ backgroundColor: tint }}>
        <p className="font-serif text-2xl tracking-tight text-[#1A1C19] leading-tight">{value}</p>
        {sub && <p className="text-sm text-[#4A4F46] mt-2 leading-relaxed">{sub}</p>}
      </div>
    </div>
  );
}
